/**
 * A supplier's order dispatch settings (batch 8 T12c1, #3033): how orders reach the supplier (by hand, by email, through the
 * supplier's system), the explicit order email address and the language of the order document.
 *
 * The form is built from the settings the server holds NOW: it is not shown until they are loaded, nothing is assumed, and only what the
 * user changed is sent (an untouched address or language is left out, so it cannot overwrite what this screen never showed; an emptied
 * one is sent as a clear). The server's real capability decides what may be chosen, and the reason a channel is unavailable is shown.
 * If the server's settings change while the form is open, saving is paused until the user loads them again.
 *
 * Reading needs `inventory.read`. Saving needs `inventory.update` AND `purchase_orders.dispatch`. A save whose outcome is unknown is not
 * repeated: the settings are read again and the outcome is shown as unknown until acknowledged.
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useDispatchAccess,
  useSupplierDispatchSettings,
  useUnconfirmedCommand,
  useUpdateSupplierDispatchSettings,
} from '@/hooks/useProcurementDispatch';
import { DispatchOwnedBoundary } from './DispatchOwnedBoundary';
import { LoadingBlock, ReadError } from './ReadFeedback';
import { apiUnavailableReason, describeDispatchError } from './dispatchErrors';
import { formatDateTimeZone } from './dispatchFormat';
import { UNSET, buildSettingsBody } from './dispatchSettingsBody';
import {
  DOCUMENT_LANGUAGES,
  SUPPLIER_DISPATCH_MODES,
  type DocumentLanguage,
  type SupplierDispatchMode,
  type SupplierDispatchSettings as Settings,
} from './dispatchTypes';

export interface SupplierDispatchSettingsProps {
  supplierId: string;
  /** Called after the server accepted a save, with the settings it now holds. */
  onSaved?: (settings: Settings) => void;
}

const SIMPLE_ADDRESS = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]{2,}$/;
const ORDER_EMAIL_MAX = 254;

function SettingsForm({
  loaded,
  latest,
  onReload,
  onSaved,
  canWrite,
}: {
  loaded: Settings;
  latest: Settings;
  onReload: () => void;
  onSaved: ((settings: Settings) => void) | undefined;
  canWrite: boolean;
}) {
  const { t, i18n } = useTranslation('procurementDispatch');
  const command = useUpdateSupplierDispatchSettings();
  const unconfirmed = useUnconfirmedCommand('settings', loaded.supplierId);
  const [mode, setMode] = useState<SupplierDispatchMode>(loaded.mode);
  const [orderEmail, setOrderEmail] = useState(loaded.orderEmail ?? '');
  const [language, setLanguage] = useState<DocumentLanguage | typeof UNSET>(
    loaded.orderDocumentLanguage ?? UNSET
  );
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const changedOnServer = latest.updatedAt !== loaded.updatedAt;
  const email = orderEmail.trim();
  const emailProblem =
    email.length > ORDER_EMAIL_MAX || (email !== '' && !SIMPLE_ADDRESS.test(email))
      ? 'invalid'
      : mode === 'email' && email === ''
        ? 'required'
        : null;
  const dirty =
    mode !== loaded.mode ||
    email !== (loaded.orderEmail ?? '') ||
    (language === UNSET ? null : language) !== loaded.orderDocumentLanguage;
  const { capability } = latest;

  const save = async () => {
    setTouched(true);
    if (emailProblem !== null || changedOnServer) return;
    setError(null);
    setSaved(false);
    setBlocked(false);
    const result = await command.run({
      supplierId: loaded.supplierId,
      body: buildSettingsBody(loaded, { mode, orderEmail, language }),
    });
    if (result.status === 'success') {
      setSaved(true);
      onSaved?.(result.result);
    } else if (result.status === 'refused') setError(result.error);
    else if (result.status === 'blocked') setBlocked(true);
  };

  const reasonText = (reason: string | undefined): string =>
    reason === undefined
      ? t('settings.unavailableUnknown')
      : t(`settings.reason.${reason}`, { defaultValue: t('settings.unavailableUnknown') });
  const apiReason = apiUnavailableReason(error);

  return (
    <Stack spacing={2.5}>
      {changedOnServer && (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={onReload}>
              {t('settings.loadLatest')}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {t('settings.changedTitle')}
          </Typography>
          <Typography variant="body2">
            {t('settings.changedBody', {
              time: formatDateTimeZone(latest.updatedAt, i18n.language),
            })}
          </Typography>
        </Alert>
      )}
      {unconfirmed.mark !== null && (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={unconfirmed.acknowledge}>
              {t('unknown.acknowledge')}
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {t('unknown.settingsTitle')}
          </Typography>
          <Typography variant="body2">{t('unknown.body')}</Typography>
        </Alert>
      )}
      {!canWrite && <Alert severity="info">{t('settings.readOnly')}</Alert>}

      <FormControl disabled={!canWrite || command.isPending}>
        <FormLabel id="dispatch-mode">{t('settings.mode')}</FormLabel>
        <RadioGroup
          aria-labelledby="dispatch-mode"
          value={mode}
          onChange={(event) => setMode(event.target.value as SupplierDispatchMode)}
        >
          {SUPPLIER_DISPATCH_MODES.map((value) => {
            // The server's capability decides. A mode the supplier already has stays visible, so it is never silently replaced.
            const channel = value === 'manual' ? null : capability[value];
            const unavailable = channel !== null && !channel.available && value !== loaded.mode;
            return (
              <Box key={value}>
                <FormControlLabel
                  value={value}
                  disabled={unavailable}
                  control={<Radio />}
                  label={t(`settings.modes.${value}`)}
                />
                <FormHelperText sx={{ mt: -1, ml: 4 }}>
                  {t(`settings.modeHints.${value}`)}
                </FormHelperText>
                {channel !== null && !channel.available && (
                  <FormHelperText sx={{ ml: 4 }} error={value === mode}>
                    {t('settings.unavailable', { reason: reasonText(channel.reason) })}
                  </FormHelperText>
                )}
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>

      <TextField
        label={t('settings.orderEmail')}
        type="email"
        value={orderEmail}
        onChange={(event) => setOrderEmail(event.target.value)}
        onBlur={() => setTouched(true)}
        disabled={!canWrite || command.isPending}
        error={touched && emailProblem !== null}
        helperText={
          touched && emailProblem === 'required'
            ? t('validation.orderEmailRequired')
            : touched && emailProblem === 'invalid'
              ? t('validation.orderEmailInvalid')
              : t('settings.orderEmailHint')
        }
        autoComplete="off"
      />
      {loaded.orderEmail === null && mode !== 'email' && (
        <Typography variant="caption" color="text.secondary">
          {t('settings.noOrderEmail')}
        </Typography>
      )}

      <TextField
        select
        label={t('settings.language')}
        value={language}
        onChange={(event) => setLanguage(event.target.value as DocumentLanguage | typeof UNSET)}
        disabled={!canWrite || command.isPending}
        helperText={
          language === UNSET
            ? t('settings.languageUnsetHint', {
                language: latest.effectiveDocumentLanguage
                  ? t(`language.${latest.effectiveDocumentLanguage}`)
                  : t('settings.languageUnusable'),
              })
            : t('settings.languageHint')
        }
      >
        <MenuItem value={UNSET}>{t('settings.languageUnset')}</MenuItem>
        {DOCUMENT_LANGUAGES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`language.${value}`)}
          </MenuItem>
        ))}
      </TextField>

      <Typography variant="caption" color="text.secondary">
        {t('settings.frozenNote')}
      </Typography>

      {blocked && (
        <Alert severity="warning" role="alert">
          {t('unknown.blockedRepeat')}
        </Alert>
      )}
      {error !== null && (
        <Alert severity="error" role="alert">
          <Typography variant="body2">{describeDispatchError(t, error)}</Typography>
          {apiReason !== null && <Typography variant="body2">{reasonText(apiReason)}</Typography>}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" role="status">
          {t('settings.saved')}
        </Alert>
      )}

      {canWrite && (
        <Box>
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={command.isPending || !dirty || changedOnServer || unconfirmed.mark !== null}
          >
            {command.isPending ? t('settings.saving') : t('settings.save')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

function SettingsContent({ supplierId, onSaved }: SupplierDispatchSettingsProps) {
  const { t } = useTranslation('procurementDispatch');
  const access = useDispatchAccess();
  const read = useSupplierDispatchSettings(supplierId);
  // The form starts from the settings as first loaded; the live query only tells it whether the server has moved on since.
  const [baseline, setBaseline] = useState<Settings | null>(null);
  const [reloads, setReloads] = useState(0);

  if (!access.canReadSettings) {
    return (
      <Alert severity="info" aria-label={t('settings.title')}>
        {t('settings.noAccess')}
      </Alert>
    );
  }
  const latest = read.data;
  // The first answer is the baseline the form starts from. It is taken once: later answers only say whether the server has moved on.
  if (latest !== undefined && (baseline === null || baseline.supplierId !== supplierId))
    setBaseline(latest);
  const loaded = baseline !== null && baseline.supplierId === supplierId ? baseline : null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label={t('settings.title')}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h3">
          {t('settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('settings.intro')}
        </Typography>
        {read.isLoading ? (
          <LoadingBlock label={t('settings.loading')} />
        ) : latest === undefined ? (
          <ReadError error={read.error} onRetry={read.refetch} />
        ) : (
          <>
            {read.isStale && (
              <Alert severity="warning" role="alert">
                {t('settings.stale')}
              </Alert>
            )}
            <SettingsForm
              key={`${supplierId}:${reloads}`}
              loaded={loaded ?? latest}
              latest={latest}
              onReload={() => {
                setBaseline(latest);
                setReloads((count) => count + 1);
              }}
              onSaved={(settings) => {
                setBaseline(settings);
                onSaved?.(settings);
              }}
              canWrite={access.canWriteSettings}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}

export function SupplierDispatchSettings(props: SupplierDispatchSettingsProps) {
  return (
    <DispatchOwnedBoundary>
      <SettingsContent key={props.supplierId} {...props} />
    </DispatchOwnedBoundary>
  );
}
